/**
 * Splash overlay — drawn on top of the app shell while React hydrates
 * and the first data query resolves. Fades out and unmounts once
 * `ready` flips to `true`.
 *
 * Why a React component instead of relying on the native PWA splash?
 *   - The native splash is frozen — a static image with no motion.
 *   - iOS <16.4 sometimes skips it entirely.
 *   - We want the SparkCast glyph pulsing, brand gradient washing,
 *     and a tiny mono caption to match the rest of the product.
 *
 * The overlay keeps itself mounted for a minimum 900ms so the first
 * paint doesn't flash and disappear on fast machines.
 */

import { useEffect, useState } from "react";
import BrandLogo from "./BrandLogo";

const MIN_VISIBLE_MS = 900;

export default function SplashOverlay() {
  const [ready, setReady] = useState(false);
  const [mounted, setMounted] = useState(true);
  const mountedAt = useState(() => Date.now())[0];

  // Hide as soon as the router/data resolves. We piggy-back on the
  // window "load" event + a microtask so the overlay doesn't outstay
  // its welcome on the dashboard (which hydrates fast).
  useEffect(() => {
    const dismiss = () => {
      const elapsed = Date.now() - mountedAt;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      window.setTimeout(() => setReady(true), wait);
    };
    if (document.readyState === "complete") dismiss();
    else window.addEventListener("load", dismiss, { once: true });
    return () => window.removeEventListener("load", dismiss);
  }, [mountedAt]);

  // Allow the fade-out to finish before we tear the node down so the
  // CSS transition plays on unmount.
  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(() => setMounted(false), 420);
    return () => window.clearTimeout(t);
  }, [ready]);

  if (!mounted) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        background:
          "radial-gradient(ellipse at 30% 20%, rgba(255,107,157,0.35), transparent 60%)," +
          "radial-gradient(ellipse at 70% 80%, rgba(197,63,255,0.30), transparent 60%)," +
          "var(--bg-page, #f7f4ed)",
        opacity: ready ? 0 : 1,
        transition: "opacity 380ms ease-out",
        pointerEvents: ready ? "none" : "auto",
      }}
    >
      {/* Card-fan brand mark (logo kit 2026-07-11). The kit forbids
          rotating the mark, so the splash breathes instead of spinning. */}
      <div
        style={{
          animation: "cl-splash-breathe 2.4s ease-in-out infinite",
          filter: "drop-shadow(0 8px 24px rgba(197,63,255,0.35))",
        }}
      >
        <BrandLogo variant="mark" size={64} />
      </div>

      <div
        style={{
          fontFamily: "var(--font-mono, 'Geist Mono Variable', ui-monospace, monospace)",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--text-tertiary, #87827a)",
          animation: "cl-splash-pulse 1.6s ease-in-out infinite",
        }}
      >
        Warming the canvas
      </div>

      <style>{`
        @keyframes cl-splash-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
        @keyframes cl-splash-pulse {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
